import logging from '../logging';

const log = logging('broadcast');

enum BroadcastState  {
    UNKNOWN,
    A_SHORTER,
    B_SHORTER,
};

/**
 * Continuously iterates over A and B, looping the shorter iterator to fill the
 * length of the longer one. If either iterator is empty, then this iterates
 * once through the non-empty iterator pairing it with null values
 */
export function* broadcast<A, B>(a: Iterable<A>, b: Iterable<B>):
    Generator<[A, B], void, void>
{
    let
        ai = a[Symbol.iterator](),
        anext = ai.next(),
        bi = b[Symbol.iterator](),
        bnext = bi.next(),
        state: BroadcastState = BroadcastState.UNKNOWN;

    // Both iterators were given to us empty
    if(anext.done && bnext.done) {
        return;
    }
    // a was empty, but b is not, so we iterate only over b
    else if(anext.done) {
        while(!bnext.done) {
            yield [null, bnext.value];
            bnext = bi.next();
        }
        return;
    }
    // b was empty, but a is not, so we only iterate over a
    else if(bnext.done) {
        while(!anext.done) {
            yield [anext.value, null];
            anext = ai.next();
        }
        return;
    }

    // If we've gotten this far, it means that we have two non-empty iterators
    // but we don't yet know which is shorter
    while(true) {
        
        yield [anext.value, bnext.value];

        anext = ai.next();
        bnext = bi.next();

        // If we still don't know who is shorter, we make that determination
        // now.
        if(state == BroadcastState.UNKNOWN) {
            if(anext.done && !bnext.done) {
                state = BroadcastState.A_SHORTER;
            }
            else if(bnext.done && !anext.done) {
                state = BroadcastState.B_SHORTER;
            }
            // If we're already done with both, it means that we had two
            // iterators of exactly length one and we have already yielded
            // their contents, so are done and return.
            else if(bnext.done && anext.done) {
                return;
            }
        }

        // When a is shorter, we check the status of b's iteration and renew
        // a's iterator when it runs out
        if(state == BroadcastState.A_SHORTER) {
            if(bnext.done) {
                return;
            }
            else if(anext.done) {
                ai = a[Symbol.iterator]();
                anext = ai.next();
            }
        }
        // Otherwise, b is shorter, so we do the obverse
        else if(state == BroadcastState.B_SHORTER) {
            if(anext.done) {
                return;
            } else if(bnext.done) {
                bi = b[Symbol.iterator]();
                bnext = bi.next();
            }
        }
    }
}
export default broadcast;

// TESTING
if(false) {
    log.warn('tests: mark block with false to remove from bundle');

    // Both empty
    const t1 = Array.from(broadcast('', ''));
    if(t1.length > 0) {
        log.error('broadcast test: t1 should be empty');
    }

    // first empty
    const t2 = Array.from(broadcast('', 'abc'));
    if(t2.length != 3) {
        log.error('broadcast test: t2.length should be 3', t2);
    }

    // both equal length
    const t3 = Array.from(broadcast('abc', 'def'));
    if(t3.length != 3) {
        log.error('broadcast test: t3.length should be 3', t3);
    }

    // right empty
    const t4 = Array.from(broadcast('abc', ''));
    if(t4.length != 3) {
        log.error('broadcast test: t4.length should be 3', t4);
    }

    // both non-empty, second longer
    const t5 = Array.from(broadcast('abc', 'defg'));
    if(t5.length != 4) {
        log.error('broadcast test: t5.length should be 4', t5);
    }

    // both non-empty, first longer
    const t6 = Array.from(broadcast('abcd', 'efg'));
    if(t6.length != 4) {
        log.error('broadcast test: t6.length should be 4', t6);
    }

    // both length 1
    const t7 = Array.from(broadcast('a', 'b'));
    if(t7.length != 1) {
        log.error('broadcast test: t7.length should be 1', t7);
    }
    
    // length of right is a multiple of the length of the left
    const t8 = Array.from(broadcast('abc', 'defghi'));
    if(t8.length != 6) {
        log.error('broadcast test: t8.length should be 6', t8);
    }
}
